import { create } from "zustand";
import type { Step, StepsData } from "@/content/schema";
import { stepStart } from "@/engine/playback";

export type Status = "idle" | "loading" | "ready" | "error";

/**
 * Playback state of the viewer. Product data comes from TanStack Query (`content/queries.ts`)
 * and is pushed here with `setData` (steps + presigned model URL); the three.js engine (`hooks/useViewer.ts`) reads this store
 * every frame.
 */
export interface ViewerState {
	slug: string | null;
	data: StepsData | null;
	/** Presigned URL of `model.glb`, set together with `data`. */
	modelUrl: string | null;
	status: Status;
	/** Model download progress, 0..1. */
	progress: number;
	error: string | null;
	stepIndex: number;
	frame: number;
	playing: boolean;
	speed: number;
	seen: ReadonlySet<string>;
	hovered: string[];
	/** Incremented to ask the engine to recentre the camera. */
	recenterRequest: number;

	startLoading: (slug: string) => void;
	setProgress: (p: number) => void;
	setData: (data: StepsData, modelUrl: string) => void;
	setError: (message: string) => void;
	goStep: (i: number) => void;
	setFrame: (f: number) => void;
	setPlaying: (p: boolean) => void;
	setSpeed: (s: number) => void;
	markSeen: (code: string) => void;
	setHovered: (names: string[]) => void;
	requestRecenter: () => void;
}

export const useViewerStore = create<ViewerState>((set, get) => ({
	slug: null,
	data: null,
	modelUrl: null,
	status: "idle",
	progress: 0,
	error: null,
	stepIndex: 0,
	frame: 1,
	playing: false,
	speed: 1,
	seen: new Set(),
	hovered: [],
	recenterRequest: 0,

	startLoading: (slug) =>
		set({
			slug,
			status: "loading",
			progress: 0,
			error: null,
			data: null,
			modelUrl: null,
			seen: new Set(),
			hovered: [],
		}),
	setProgress: (progress) => set({ progress }),
	setData: (data, modelUrl) => {
		const first = data.steps[0];
		set({
			data,
			modelUrl,
			status: "loading",
			progress: 0,
			stepIndex: 0,
			frame: first ? stepStart(first) : 1,
			playing: false,
		});
	},
	setError: (error) => set({ status: "error", error }),
	goStep: (i) => {
		const { data, stepIndex, frame, seen } = get();
		if (!data) return;
		const next = Math.max(0, Math.min(data.steps.length - 1, i));
		const current = data.steps[stepIndex];
		const target = data.steps[next];
		if (!current || !target) return;
		const newSeen = new Set(seen);
		if (frame >= current.f1) newSeen.add(current.code);
		set({
			stepIndex: next,
			frame: stepStart(target),
			playing: false,
			seen: newSeen,
			hovered: [],
		});
	},
	setFrame: (frame) => set({ frame }),
	setPlaying: (playing) => set({ playing }),
	setSpeed: (speed) => set({ speed }),
	markSeen: (code) => set((s) => ({ seen: new Set(s.seen).add(code) })),
	setHovered: (hovered) => set({ hovered }),
	requestRecenter: () =>
		set((s) => ({ recenterRequest: s.recenterRequest + 1 })),
}));

export const selectStep = (s: ViewerState): Step | null =>
	s.data?.steps[s.stepIndex] ?? null;

/** Number of numbered steps (the last numbered entry is the "final" pseudo-step). */
export const selectStepCount = (s: ViewerState): number =>
	s.data ? s.data.steps.filter((st) => st.num > 0).length - 1 : 0;
